"use client";

import React from "react";
import { useNavigate } from "react-router-dom";
import type {
  ILoginContext,
  IProps,
  SignInProps,
} from "../@types/context/loginContext.structure";
import loginService from "@services/Login/loginService";
import { datadogRum } from "@datadog/browser-rum";
import notifyServiceInstance from "@services/Notify/notifyService";

// Criação do contexto com um valor padrão vazio
export const LoginContext = React.createContext({} as ILoginContext);

// Hooks personalizados para acessar apenas partes específicas do contexto
export const useLoginAuth = () => {
  const context = React.useContext(LoginContext);
  return {
    user: context.user,
    isAuthenticated: context.isAuthenticated,
    signIn: context.signIn,
    signOut: context.signOut,
    errorLogin: context.errorLogin,
    setErrorLogin: context.setErrorLogin,
    loadingLogin: context.loadingLogin,
    isRemakeLogin: context.isRemakeLogin,
    setIsRemakeLogin: context.setIsRemakeLogin,
  };
};

export const useLoginNavigation = () => {
  const context = React.useContext(LoginContext);
  return {
    pageSidebar: context.pageSidebar,
    setPageSidebar: context.setPageSidebar,
    openSidebar: context.openSidebar,
    setOpenSidebar: context.setOpenSidebar,
    page: context.page,
    setPage: context.setPage,
    pageHelper: context.pageHelper,
    setPageHelper: context.setPageHelper,
    hashLogin: context.hashLogin,
    setHashLogin: context.setHashLogin,
  };
};

export const useLoginNotifications = () => {
  const context = React.useContext(LoginContext);
  return {
    notifications: context.notifications,
    setNotifications: context.setNotifications,
    isMarkedNotifications: context.isMarkedNotifications,
    setIsMarkedNotifications: context.setIsMarkedNotifications,
    fetchNotifications: context.fetchNotifications,
  };
};

export const useLoginProfile = () => {
  const context = React.useContext(LoginContext);
  return {
    profile: context.profile,
    setProfile: context.setProfile,
    regional: context.regional,
    setRegional: context.setRegional,
    company: context.company,
    setCompany: context.setCompany,
    email: context.email,
  };
};

const LoginContextProvider: React.FC<IProps> = ({ children }) => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("@token");
  const [user, setUser] = React.useState<boolean>(false);
  const isAuthenticated = user;
  const [profile, setProfile] = React.useState([]);
  const [regional, setRegional] = React.useState(
    localStorage.getItem("@regional")
  );
  const [company, setCompany] = React.useState(
    localStorage.getItem("@empresa")
  );
  const [errorLogin, setErrorLogin] = React.useState<boolean>(false);
  const [loadingLogin, setLoadingLogin] = React.useState<boolean>(false);
  const [openSidebar, setOpenSidebar] = React.useState<boolean>(true);
  const [errorForgotPassword, setErrorForgotPassword] =
    React.useState<boolean>(false);
  const [hashLogin, setHashLogin] = React.useState<string>("CurrentLogin");
  const [page, setPage] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>("");
  const [pageHelper, setPageHelper] = React.useState<string>("");
  const [pageSidebar, setPageSidebar] = React.useState<string>("");

  const [isRemakeLogin, setIsRemakeLogin] = React.useState(false);
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [isMarkedNotifications, setIsMarkedNotifications] =
    React.useState(false);

  const fetchNotifications = React.useCallback(async () => {
    try {
      const response = await notifyServiceInstance.getNotify();

      if (response.status >= 400 && response?.status !== 401) {
        setNotifications([]);
        console.log("status 400", response);
        return;
      }
      if (response.status === 401) {
        setNotifications([]);
        return;
      }

      // Verificar se há alguma notificação com exibido === null
      const hasUnreadNotifications = Array.isArray(response)
        ? response.some((notification) => notification.exibido === null)
        : false;

      setIsMarkedNotifications(hasUnreadNotifications);

      setNotifications(Array.isArray(response) ? response : []);
    } catch (error) {
      console.log("Erro ao buscar notificações:", error);
      setNotifications([]);
    }
  }, []);

  const signIn = React.useCallback(
    async ({ email, password }: SignInProps) => {
      setLoadingLogin(true);
      try {
        const response = await loginService.postLogin({
          email,
          senha: password,
        });

        if (response?.data?.titulo === "Dado divergente") {
          console.log("Dado divergente", response);
          setErrorLogin(true);
          setLoadingLogin(false);
          return;
        } else if (response?.data?.deveRefazerPrimeiroAcesso) {
          setIsRemakeLogin(true);
          setLoadingLogin(false);
          return;
        } else if (response?.status >= 400) {
          console.log("errorLogin", response);
          setErrorLogin(true);
          setLoadingLogin(false);
          return;
        } else {
          const { access_token, idToken } = response;

          sessionStorage.setItem("@token", access_token);
          sessionStorage.setItem("@tokenNotification", idToken);
          localStorage.setItem("@tipoDeUsuario", response?.tipoDeUsuario);
          localStorage.setItem("@idCorretor", response?.idCorretorSalesforce);
          localStorage.setItem("@email", email);
          localStorage.setItem("@user", response?.nomeCorretor);
          localStorage.setItem("@regional", response?.regional);
          localStorage.setItem("@regiao", response?.regiao);
          localStorage.setItem("@empresa", response?.empresa);
          localStorage.setItem("@cpf", response?.cpf);
          localStorage.setItem("@dtNasc", response?.dataNascimento);
          localStorage.setItem("@option", "Home");
          setPageSidebar("Home");
          // localStorage.setItem("@option", "Criar oportunidade");
          // setPageSidebar("Criar oportunidade");

          datadogRum.setUser({
            id: response?.idCorretorSalesforce,
            name: response?.nomeCorretor,
            email: email,
            regional: response?.regional,
            empresa: response?.empresa,
            regiao: response?.regiao,
            cpf: response?.cpf,
          });

          setRegional(response?.regional);
          setCompany(response?.empresa);
          setEmail(email);
          setUser(true);
          navigate("/");
          // navigate("/oportunidades");
          setLoadingLogin(false);
          setErrorLogin(false);
          setPage("");
          await fetchNotifications();
        }
      } catch (error) {
        setLoadingLogin(false);
        console.log("ERRO AO FAZER LOGIN ", error);
      }
    },
    [navigate, setPageSidebar, fetchNotifications]
  );

  const refreshPage = React.useCallback(() => {
    setTimeout(() => {
      window.location.reload();
      sessionStorage.removeItem("@token");
      localStorage.setItem("@option", "Criar oportunidade");
      datadogRum.clearUser();
    }, 500);
  }, []);

  const signOut = React.useCallback(() => {
    try {
      refreshPage();
    } catch {
      console.log("erro ao deslogar");
    }
  }, [refreshPage]);

  React.useEffect(() => {
    if (!token) return;

    const fetchIfActive = () => {
      if (document.hidden) return;
      fetchNotifications();
    };

    fetchIfActive();

    // Configura o intervalo em 1h
    const interval = setInterval(fetchIfActive, 3600000);

    // Adiciona evento para atualizar quando a aba fica visível
    const handleVisibilityChange = () => {
      if (!document.hidden) return fetchNotifications();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, fetchNotifications]);

  // Memoize o valor do contexto para evitar re-renderizações desnecessárias
  const contextValue = React.useMemo(
    () => ({
      setIsRemakeLogin,
      fetchNotifications,
      notifications,
      setNotifications,
      isMarkedNotifications,
      setIsMarkedNotifications,
      regional,
      setRegional,
      setCompany,
      company,
      hashLogin,
      setHashLogin,
      user,
      setUser,
      email,
      openSidebar,
      setOpenSidebar,
      isAuthenticated,
      page,
      setPage,
      pageHelper,
      setPageHelper,
      profile,
      setProfile,
      signIn,
      signOut,
      errorLogin,
      setErrorLogin,
      loadingLogin,
      errorForgotPassword,
      setErrorForgotPassword,
      pageSidebar,
      setPageSidebar,
      isRemakeLogin,
    }),
    [
      setIsRemakeLogin,
      fetchNotifications,
      notifications,
      setNotifications,
      isMarkedNotifications,
      setIsMarkedNotifications,
      regional,
      setRegional,
      setCompany,
      company,
      hashLogin,
      setHashLogin,
      user,
      setUser,
      email,
      openSidebar,
      setOpenSidebar,
      isAuthenticated,
      page,
      setPage,
      pageHelper,
      setPageHelper,
      profile,
      setProfile,
      signIn,
      signOut,
      errorLogin,
      setErrorLogin,
      loadingLogin,
      errorForgotPassword,
      setErrorForgotPassword,
      pageSidebar,
      setPageSidebar,
      isRemakeLogin,
    ]
  );

  return (
    <LoginContext.Provider value={contextValue}>
      {children}
    </LoginContext.Provider>
  );
};

export default React.memo(LoginContextProvider);
